/**
 * Google Analytics 4 (gtag.js, loaded directly — no GTM) for the viewer SPAs.
 *
 * This is the client-side ACQUISITION/attribution signal — where a visitor came from and how they
 * moved across the marketing site and the app. It is DISTINCT from the server-side upgrade funnel
 * (`analytics/funnelClient` → POST /analytics/funnel), which owns money-truth and must never be
 * duplicated here.
 *
 * Which property receives the data is decided by the running HOST, exactly like `apiBaseUrl()` in
 * `api/config`: the `.com` registrable domain is production; everything else (the `.uk` staging rig)
 * is dev/test. Both Measurement IDs are PUBLIC — they ship in every client bundle — so baking both
 * in and picking by hostname is safe and needs zero per-environment build config. In `vite dev`
 * there is no build of anything to measure, so analytics stays fully OFF.
 *
 * Marketing → app session stitching is automatic: in production the Lovable site (yoreflix.com) and
 * the app (`*.yoreflix.com`) share one registrable domain, so GA's `_ga` cookie is shared and the
 * journey is one session with NO cross-domain configuration.
 *
 * Consent Mode v2: analytics_storage defaults to DENIED until the visitor decides (see
 * `components/ConsentBanner`). A prior decision is remembered in localStorage, shared across the
 * same-origin host + viewermain frames.
 */

// PUBLIC Measurement IDs (they ship in the bundle). Prod = Yoreflix — prod (yoreflix.com);
// dev = Yoreflix — dev/test (yoreflix.uk). See memory: yoreflix-google-analytics.
const GA_ID_PROD = 'G-X12RTN99BS'
const GA_ID_DEV = 'G-83ZC3EZEFX'

const CONSENT_KEY = 'yf_ga_consent' // localStorage: 'granted' | 'denied'

type GtagFn = (...args: unknown[]) => void

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: GtagFn
  }
}

/** The Measurement ID for a given host — `.com` is production, everything else is dev/test. */
export function measurementIdForHost(hostname: string): string {
  const isCom = hostname === 'yoreflix.com' || hostname.endsWith('.yoreflix.com')
  return isCom ? GA_ID_PROD : GA_ID_DEV
}

/**
 * The Measurement ID for THIS document, or '' when analytics must stay off. Off in `vite dev`
 * (a dev bundle measures nothing). `VITE_GA_MEASUREMENT_ID` overrides for throwaway hosts.
 */
export function gaMeasurementId(): string {
  if (import.meta.env.DEV) return ''
  const override = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined
  if (override && override.trim()) return override.trim()
  return measurementIdForHost(window.location.hostname)
}

/** True when a Measurement ID resolves — i.e. GA will actually load on this host. */
export function gaEnabled(): boolean {
  return gaMeasurementId() !== ''
}

/** The platform dimension attached to every hit (`?os=` is authoritative; see resolvePlatform). */
function platformTag(): string {
  try {
    const os = new URLSearchParams(window.location.search).get('os')?.trim().toLowerCase()
    return os || 'web'
  } catch {
    return 'web'
  }
}

/** The remembered consent decision, or null if the visitor has not decided yet. */
export function consentDecision(): 'granted' | 'denied' | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY)
    return v === 'granted' || v === 'denied' ? v : null
  } catch {
    return null
  }
}

/** True once the visitor has accepted or rejected — drives whether the banner shows. */
export function hasConsentDecision(): boolean {
  return consentDecision() !== null
}

/**
 * DebugView switch — routes THIS browser's hits to GA4 DebugView (Admin → DebugView), where each
 * event is shown with all its parameters. Turn on with `?ga_debug=1`, off with `?ga_debug=0`. The
 * choice is persisted to localStorage so it survives the host→viewermain iframe hop (the iframe does
 * not inherit the host's query string) and later plain loads. Affects only this browser, never other
 * visitors, and does not change what is collected — only where it is visible.
 */
export function gaDebugEnabled(): boolean {
  try {
    const p = new URLSearchParams(window.location.search).get('ga_debug')
    if (p === '1') {
      localStorage.setItem('yf_ga_debug', '1')
      return true
    }
    if (p === '0') {
      localStorage.removeItem('yf_ga_debug')
      return false
    }
    return localStorage.getItem('yf_ga_debug') === '1'
  } catch {
    return false
  }
}

let initialized = false

/**
 * Load gtag.js once for this document and configure GA4 with Consent Mode v2. No-op when there is
 * no Measurement ID (local dev / non-yoreflix host) or when already initialized. `page_view` is NOT
 * sent automatically — the SPA fires it per route via `trackPageView` (send_page_view: false).
 */
export function initGa(): void {
  if (initialized) return
  const id = gaMeasurementId()
  if (!id) return
  initialized = true

  window.dataLayer = window.dataLayer || []
  const gtag: GtagFn = function () {
    // The canonical gtag stub pushes the raw `arguments` object; gtag.js reads it back verbatim.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  }
  window.gtag = gtag

  // Default everything denied until the visitor decides; a prior 'granted' (shared localStorage)
  // starts granted so returning visitors are not re-prompted-then-blocked.
  const prior = consentDecision()
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: prior === 'granted' ? 'granted' : 'denied',
  })

  gtag('js', new Date())
  const config: Record<string, unknown> = { send_page_view: false }
  if (gaDebugEnabled()) config.debug_mode = true // ?ga_debug=1 → visible in GA4 DebugView
  gtag('config', id, config)

  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  document.head.appendChild(s)
}

/** Grant analytics consent (Consent Mode update) and remember it across visits. */
export function grantConsent(): void {
  try {
    localStorage.setItem(CONSENT_KEY, 'granted')
  } catch {
    /* private mode / storage blocked — consent simply won't persist */
  }
  window.gtag?.('consent', 'update', { analytics_storage: 'granted' })
}

/** Deny analytics consent and remember it across visits. */
export function denyConsent(): void {
  try {
    localStorage.setItem(CONSENT_KEY, 'denied')
  } catch {
    /* ignore */
  }
  window.gtag?.('consent', 'update', { analytics_storage: 'denied' })
}

/**
 * Fire a GA4 page_view for an in-app route. `path` is the app-facing path (e.g. "/categories"),
 * already normalized away from the raw iframe hash URL so reports read cleanly. No-op until initGa
 * has run (calls before the gtag stub exists are simply dropped — page views are best-effort).
 */
export function trackPageView(path: string, title?: string): void {
  if (!window.gtag) return
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title ?? document.title,
    platform: platformTag(),
  })
}

/** Fire an arbitrary GA4 event, always tagged with the platform dimension. */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!window.gtag) return
  window.gtag('event', name, { platform: platformTag(), ...params })
}
